/**
 * @module lib/acs/minutesSchema
 * @description Schemas Zod para validar y parsear minutas de sesión generadas por Gemini.
 *
 * Al finalizar una sesión ACS, Gemini genera un texto estructurado con el resumen,
 * decisiones clave, ítems de acción, riesgos identificados y tareas propuestas.
 * Este módulo valida ese output antes de persistirlo en la base de datos.
 *
 * ## Flujo de procesamiento de minutas
 * 1. Gemini retorna `rawText` (siempre guardado, independiente de si parsea bien)
 * 2. `parseMinutesContent()` extrae el bloque JSON del texto (dentro de ` ```json ... ``` `)
 * 3. Zod valida la estructura con `MinutesContentSchema`
 * 4. Si valida OK → `status='final'`, `proposedTasks` disponibles para revisión del CISO
 * 5. Si Zod falla → `status='needs_review'`, `parseError` guardado para diagnóstico
 *
 * ## Seguridad de tareas propuestas
 * Las tareas propuestas (`ProposedTaskSchema`) pasan por revisión manual del CISO
 * antes de crear tareas reales en el sistema. El módulo solo estructura los datos;
 * la aprobación ocurre en `ACSProposalReview.tsx`.
 *
 * @see acsAI.ts que invoca la generación de minutas al finalizar la sesión
 * @see ACSContext.tsx que llama a `parseMinutesContent()` y guarda el resultado
 * @see types/acs.ts para los tipos ACSMinutes y ACSProposedTaskData
 */
// ============================================================
// Zod Schemas para validación de minutas generadas por Gemini
//
// FLUJO:
// 1. Gemini retorna texto plano (rawText) — SIEMPRE guardado.
// 2. parseMinutesContent() intenta extraer JSON del rawText.
// 3. Si Zod valida OK → status='final', proposedTasks disponibles.
// 4. Si Zod falla → status='needs_review', parseError guardado,
//    NO se generan propuestas automáticas.
// ============================================================

import { z } from 'zod';
import type { ACSProposedTaskData, ACSMinutes } from '../../types/acs';

// ──────────────────────────────────────────────────────────
// SCHEMAS
// ──────────────────────────────────────────────────────────

export const ProposedTaskSchema = z.object({
  title:                z.string().min(3).max(200),
  description:          z.string().max(2000).optional().default(''),
  priority:             z.enum(['low', 'medium', 'high', 'critical']),
  estimatedDueDate:     z.string().optional(),
  suggestedAssigneeId:  z.string().uuid().optional(),
});

export const MinutesContentSchema = z.object({
  summary:         z.string().min(10).max(5000),
  keyDecisions:    z.array(z.string().max(500)).max(20).default([]),
  actionItems:     z.array(z.string().max(500)).max(30).default([]),
  risksIdentified: z.array(z.string().max(500)).max(20).default([]),
  proposedTasks:   z.array(ProposedTaskSchema).max(10).default([]),
});

export type MinutesContent = z.infer<typeof MinutesContentSchema>;

// ──────────────────────────────────────────────────────────
// PARSER CON FALLBACK
// ──────────────────────────────────────────────────────────

export interface ParseMinutesResult {
  ok:     boolean;
  data?:  MinutesContent;
  error?: string;
}

/**
 * Intenta parsear el texto de Gemini como MinutesContent.
 * Gemini puede devolver JSON dentro de ```json ... ``` o como texto plano.
 */
export function parseMinutesContent(rawText: string): ParseMinutesResult {
  try {
    // Intentar extraer bloque ```json ... ```
    const jsonBlockMatch = rawText.match(/```json\s*([\s\S]*?)```/);
    const jsonStr = jsonBlockMatch
      ? jsonBlockMatch[1].trim()
      : rawText.trim();

    const parsed = JSON.parse(jsonStr);
    const result = MinutesContentSchema.safeParse(parsed);

    if (result.success) {
      return { ok: true, data: result.data };
    }

    return {
      ok:    false,
      error: `Zod validation failed: ${result.error.issues.map(i => `${i.path.join('.')}: ${i.message}`).join('; ')}`,
    };
  } catch (e) {
    return {
      ok:    false,
      error: `JSON parse failed: ${String(e)}`,
    };
  }
}

// ──────────────────────────────────────────────────────────
// PROMPT PARA GENERACIÓN DE MINUTAS
// ──────────────────────────────────────────────────────────

/**
 * Construye el prompt para que Gemini genere una minuta estructurada.
 * La respuesta DEBE ser JSON válido envuelto en ```json ... ```.
 */
export function buildMinutesPrompt(
  contextLabel: string,
  frameworkCode: string,
  messages: Array<{ role: string; content: string }>
): string {
  const transcript = messages
    .map(m => `[${m.role === 'user' ? 'Contraparte' : 'Asistente IA'}]: ${m.content}`)
    .join('\n\n');

  return `Eres un asistente de gobernanza ISO especializado en documentación de reuniones de cumplimiento.

Se te proporciona la transcripción completa de una sesión ACS para:
- Framework: ${frameworkCode}
- Control/Requisito/Artículo: ${contextLabel}

TRANSCRIPCIÓN:
${transcript}

---

Genera una minuta estructurada en formato JSON ESTRICTO, envuelta en \`\`\`json ... \`\`\`.
El JSON debe seguir EXACTAMENTE esta estructura:

{
  "summary": "Resumen ejecutivo de la sesión (mínimo 10 caracteres)",
  "keyDecisions": ["Decisión 1", "Decisión 2"],
  "actionItems": ["Acción 1", "Acción 2"],
  "risksIdentified": ["Riesgo 1", "Riesgo 2"],
  "proposedTasks": [
    {
      "title": "Título de la tarea (entre 3 y 200 caracteres)",
      "description": "Descripción detallada",
      "priority": "low | medium | high | critical",
      "estimatedDueDate": "YYYY-MM-DD (opcional)",
      "suggestedAssigneeId": null
    }
  ]
}

RESTRICCIONES:
- proposedTasks máximo 10 elementos
- keyDecisions máximo 20 elementos
- actionItems máximo 30 elementos
- risksIdentified máximo 20 elementos
- NO inventar información que no esté en la transcripción
- Si no hay decisiones/acciones/riesgos claros, devolver arrays vacíos
- NO incluir texto fuera del bloque JSON`;
}

// ──────────────────────────────────────────────────────────
// CONVERSIÓN A FORMATO ACSMinutes (parcial, sin campos de DB)
// ──────────────────────────────────────────────────────────

export function minutesContentToPartialACSMinutes(
  content: MinutesContent
): Pick<ACSMinutes, 'summary' | 'keyDecisions' | 'actionItems' | 'risksIdentified' | 'proposedTasks'> {
  return {
    summary:         content.summary,
    keyDecisions:    content.keyDecisions,
    actionItems:     content.actionItems,
    risksIdentified: content.risksIdentified,
    proposedTasks:   content.proposedTasks as ACSProposedTaskData[],
  };
}
